import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  ManagementSection,
  type ManagementSectionProps,
} from '@openx/components-core/lib/Pagination/ManagementSection.tsx';
import type { ConstValues } from '@openx/types/helpers.ts';
import { type AllTableCriteria, type ChangeCriteriaHandler, CriteriaDimension } from '@openx/types/tableCriteria.ts';
import type {
  BaseRow,
  Columns,
  RowAction,
  RowActionButtonBaseProps,
  RowActionProps,
} from '@openx/types/tableHelpers.ts';
import { prepareChangeCriteriaHandler } from '@openx/utils/lib/tableHelpers.ts';
import { type ComponentType, type JSX, type ReactElement, type ReactNode, useEffect, useMemo, useState } from 'react';
import type { HighlightRules } from './highlightRules.ts';
import { DataSection, type DataSectionProps, type NoDataComponentProps } from './parts/sections/DataSection.tsx';
import type { TableRowPropsWithData } from './parts/TableRow.tsx';
import type { TileViewLabel } from './TileViewGrid/TileViewHeadLabels/TileViewHeadLabels.tsx';

export const TableName = {
  AUDIENCES: 'audiences',
  GRANULAR_FEE_CAPS: 'granular-fee-caps',
  RECENT_TRACES: 'recent-traces',
  TRACE_LOGS: 'trace-logs',
  DEAL_LIBRARY: 'deal-library',
  GENERATED_TRACES: 'generated-traces',
  AD_FILTERS: 'ad_filters',
  CREATIVES: 'creatives',
  CREATIVES_MANAGEMENT: 'creatives_management',
  ACCOUNTS: 'accounts',
  ACCOUNTS_DSP: 'accounts-dsp',
  ADUNITS: 'adunits',
  AQ_RULES: 'aq_rules',
  AQ_RULES_REPORTS: 'aq_rules_reports',
  AQ_RULES_SIMULATION: 'aq_rules_simulation',
  APPS: 'apps',
  BRAND_FLOORS: 'brand_floors',
  PRICING_RULES: 'pricing_rules',
  BRANDS: 'brands',
  BRAND_FEE_CAPS: 'brand_fee_caps',
  BUYERS: 'buyers',
  COPPA_FLAGGED: 'coppa_flagged',
  COPPA_UNFLAGGED: 'coppa_unflagged',
  DEALS: 'deals',
  DOMAINS: 'domains',
  IMPERSONATE: 'impersonate',
  INSTANCES: 'instances',
  MARKET_TOOLS: 'market_tools',
  METACATEGORIES: 'metacategories',
  PACKAGES: 'packages',
  TRAFFIC_SETS: 'traffic_sets',
  PARTNERS: 'partners',
  PUBLISHERS: 'publishers',
  SEATS: 'seats',
  SITES: 'sites',
  TS_DECODER: 'ts_decoder',
  USED_BY_ENTRY: 'used_by_entry',
  USERS: 'users',
  GRANULAR_DSP_FEE_CAPS: 'granular_dsp_fee_caps',
  PUBLISHER_FEE_CAPS: 'publisher_fee_caps',
  ORDERS: 'orders',
  AUCTION_PACKAGES: 'auction_packages',
  SEGMENTS: 'segments',
  SEAT_FEE_CAPS: 'seat_fee_caps',
  CONFIRMATION_DIALOG_TABLE: 'confirmation-dialog',
  PROVIDERS: 'providers',
  POST_AUCTION_DISCOUNTS: 'post_auction_discounts',
  ASSOCIATED_AUDIENCES_DIALOG: 'associated-audiences-dialog',
  ORGANIZATIONS: 'organizations',
  LINKED_PROVIDERS: 'linked_providers',
  API_KEYS: 'api_keys',
  PIXEL_METRICS: 'pixel_metrics',
  REBATES: 'rebates',
  DIRECT_DEALS: 'direct_deals',
  INVENTORY_LISTS: 'inventory_lists',
  TAXONOMY_LISTS: 'taxonomy_lists',
  ASSOCIATED_AUDIENCES: 'associated_audiences',
  ARCHIVE_SEGMENTS: 'archive_segments',
  GENERIC_TABLE: 'generic_table',
  TABLE_DATA_SECTION: 'table_data_section',
  DEAL_INSIGHTS_GENERAL: 'deal_insights_general',
  LAL_SEGMENTS: 'lal_segments',
  DEAL_INSIGHTS_DELIVERY_BY_DOMAIN_BUNDLE: 'deal_insights_delivery_by_domain_bundle',
  DEAL_INSIGHTS_DELIVERY_BY_AD_SIZE: 'deal_insights_delivery_by_ad_size',
  DEAL_INSIGHTS_AUDIENCES: 'deal_insights_audiences',
  DEAL_INSIGHTS_GEOLOCATION: 'deal_insights_geolocation',
  LAL_SEGMENT_REACH: 'lal_segment_reach',
  CURATED_DEAL_BUYER_DISCOUNTS: 'curated_deal_buyer_discounts',
  PMP_FEE_SCHEDULES: 'pmp_fee_schedules',
  DEAL_PROMPT_DOMAINS: 'deal_prompt_domains',
  DEAL_PROMPT_BUNDLES: 'deal_prompt_bundles',
  DEAL_TDD_SYNC_ERRORS: 'deal_tdd_sync_errors',
  DEAL_AMAZON_SYNC_ERRORS: 'deal_amazon_sync_errors',
  DEAL_INSIGHTS_DELIVERY_BY_AD_DURATION: 'deal_insights_delivery_by_ad_duration',
  DEAL_PROMPT_AUDIENCES: 'deal_prompt_audiences',
  BLOCKED_SEATS: 'blocked_seats',
  COMMERCIAL_AGREEMENTS: 'commercial_agreements',
  DEALS_SENSITIVE_CATEGORIES_PROPOSED: 'deals_sensitive_categories_proposed',
  DEALS_SENSITIVE_CATEGORIES_DECIDED: 'deals_sensitive_categories_decided',
} as const;
export type TableName = ConstValues<typeof TableName>;

export interface TableProps<RowT extends BaseRow, FiltersT extends Record<string, unknown> = Record<string, unknown>> {
  name: TableName;
  data: RowT[];
  columns: Columns<RowT>;
  onRowClick?: RowAction<RowT>;
  disabledItems?: RowT[];
  loading: boolean;
  noItemsLabel?: (data: RowT) => string;
  isTree?: boolean;
  fetchTreeChildren?: (data: RowT) => Promise<RowT[]>;
  isForcedExpanded?: (data: RowT) => boolean;
  treeLevelItems?: number;
  criteria?: AllTableCriteria<FiltersT>;
  onCriteriaChange?: ChangeCriteriaHandler<CriteriaDimension, FiltersT>;
  highlightRules?: HighlightRules;
  customManagementSection?: ReactNode;
  topManagementSection?: ReactNode;
  primaryRowAction?: RowActionButtonBaseProps<RowT>;
  hidePrimaryRowAction?: boolean;
  secondaryRowAction?: RowActionButtonBaseProps<RowT>;
  dangerRowAction?: RowActionButtonBaseProps<RowT>;
  defaultRowAction?: RowActionButtonBaseProps<RowT>;
  customRowActions?: RowActionButtonBaseProps<RowT>[];
  rowActions?: RowActionProps<RowT>[];
  stickyTable?: boolean;
  enableTableHeaderCustomization?: boolean;
  renderCustomRow?: (rowProps: TableRowPropsWithData<RowT>, isForcedExpanded?: (data: RowT) => boolean) => ReactElement;
  loadingOnInit?: boolean;
  overrideTableCellStyles?: SxProps<Theme>;
  groupBy?: keyof RowT;
  forceExpandGroupedItems?: boolean;
  tileViewEnabled?: boolean;
  groupedItemsActions?: React.ReactNode;
  TileViewComponent?: ComponentType<{ item: RowT }>;
  TileViewLoadingComponent?: ComponentType;
  tileViewHeaderLabels?: TileViewLabel[];
  'data-test'?: string;
  getRowKey?: (row: RowT, index?: number) => number | string;
  skeletonRows?: number;
  customNoDataComponent?: ComponentType<NoDataComponentProps<RowT>>;
  stickyActionColumn?: boolean;
}

export const Table = <RowT extends BaseRow, FiltersT extends Record<string, unknown> = Record<string, unknown>>({
  criteria,
  enableTableHeaderCustomization = true,
  highlightRules,
  onCriteriaChange,
  loadingOnInit,
  loading,
  forceExpandGroupedItems,
  groupedItemsActions,
  hidePrimaryRowAction = false,
  tileViewEnabled = false,
  TileViewComponent,
  TileViewLoadingComponent,
  tileViewHeaderLabels,
  'data-test': dataTest,
  getRowKey,
  skeletonRows,
  customNoDataComponent,
  ...props
}: TableProps<RowT, FiltersT>): JSX.Element => {
  const [initialized, setInitialized] = useState(!loadingOnInit || props.data.length > 0);

  const { onPaginationChange, onSortChange } = useMemo(
    () => ({
      onPaginationChange: prepareChangeCriteriaHandler(CriteriaDimension.PAGINATION, onCriteriaChange),
      onSortChange: prepareChangeCriteriaHandler(CriteriaDimension.SORT, onCriteriaChange),
    }),
    [onCriteriaChange]
  );

  useEffect(() => {
    // from react 18 we need to use this in redux component to avoid displaying no results found on first render
    loadingOnInit && setInitialized(true);
  }, [loadingOnInit]);

  const primaryRowAction = hidePrimaryRowAction
    ? { primaryRowAction: undefined }
    : { primaryRowAction: props.primaryRowAction };

  const managementSectionProps: ManagementSectionProps = {
    ...props,
    ...primaryRowAction,
    loading: loading || !initialized,
    onPaginationChange,
    paginationCriteria: criteria?.pagination,
  };

  const dataSectionProps: DataSectionProps<RowT> = {
    ...props,
    ...primaryRowAction,
    enableTableHeaderCustomization,
    forceExpandGroupedItems,
    groupedItemsActions,
    highlightRules,
    loading: loading || !initialized,
    onSortChange,
    sortCriteria: criteria?.sort,
    tileViewEnabled,
    TileViewComponent,
    TileViewLoadingComponent,
    tileViewHeaderLabels,
    'data-test': dataTest,
    getRowKey,
    skeletonRows,
    customNoDataComponent,
  };

  return (
    <Box data-test={dataTest}>
      {props.topManagementSection && (
        <ManagementSection {...managementSectionProps} customManagementSection={props.topManagementSection} />
      )}
      <DataSection {...dataSectionProps} />
      <ManagementSection {...managementSectionProps} />
    </Box>
  );
};
